import { useMutation } from "@apollo/client";
import { createContext, Dispatch, useEffect, useMemo, useReducer } from "react";

import { RankifierAction, RankifierGame, rankifierReducer, RankifierState } from "../../reducers/rankifier";
import { RESPONSE_CREATE } from "../../graphql/mutations/Response";
import { useGlobalConfiguration } from "../../hooks/useGlobalConfiugration";
import { shuffleArray } from "../../utils";
import { integrationApi } from "../../main";
import { ResponseKind } from "../../gql/graphql";

export const RankifierStateContext = createContext<[RankifierState, Dispatch<RankifierAction>] | null>(null);

interface RankifierStateProviderProps {
  children: React.ReactNode;
  game: RankifierGame;
  onGameFinished: (newResponseId?: string) => void;
}

export const RankifierStateProvider = (props: RankifierStateProviderProps) => {
  const { children, game, onGameFinished } = props;
  const { attributes, cardsPerHand, numberOfHands } = game.configuration;
  const [responseCreate] = useMutation(RESPONSE_CREATE);
  const { isPreviewMode } = useGlobalConfiguration();

  const cards = useMemo(() => {
    // [1, 2, 3, 4] to eg. [4, 2, 3, 1]
    const shuffledAttributes = shuffleArray(attributes);

    // eg. [[2, 3, 1, 4], [4, 1, 3, 2], [3, 2, 4, 1], [1, 4, 2, 3]]
    const handsToRender = Array.from({ length: numberOfHands }, (_, handIndex) => {
      // eg. [2, 3, 1, 4]
      return shuffleArray(
        Array.from({ length: cardsPerHand }, (_, cardIndex) => {
          return shuffledAttributes[(handIndex * cardsPerHand + cardIndex) % shuffledAttributes.length];
        }),
      );
    });

    // eg. [4, 2, 3, 1, 4, 1, 3, 2, 3, 2, 4, 1, 1, 4, 2, 3]
    const attributesToRender = shuffleArray(handsToRender).flat();

    // turn into [{ attribute: <Attribute>, index: 0 }, ...]
    return attributesToRender.map((attribute, index) => ({
      attribute,
      index,
    }));
  }, [attributes, numberOfHands, cardsPerHand]);

  const [state, dispatch] = useReducer(rankifierReducer, {
    cards: {
      all: [...cards],
      deck: [...cards],
      hand: new Array(cardsPerHand).fill(null),
      selected: new Array(numberOfHands).fill(null),
    },
    status: {
      round: 0,
      isPickingFavorite: true,
      shouldChangeRound: true,
      isGameFinished: false,
      startTime: new Date(),
    },
    results: attributes.reduce(
      (acc, { id }) => ({
        ...acc,
        [id]: {
          minuses: 0,
          plusses: 0,
          zeroes: 0,
        },
      }),
      {},
    ),
    game: {
      ...game,
      configuration: {
        ...game.configuration,
        numberOfHands: numberOfHands,
      },
    },
  });

  const saveResponse = async () => {
    if (isPreviewMode) {
      return onGameFinished("");
    }

    const { results } = state;

    const responseKind =
      integrationApi.configuration.provider === "alchemer" ? ResponseKind.Alchemer : ResponseKind.Hosted;

    const { data } = await responseCreate({
      variables: {
        data: {
          responseKind,
          startTime: state.status.startTime.toISOString(),
          endTime: new Date().toISOString(),
          gameId: game.id,
          rankifierScores: Object.keys(results).map((attributeId) => ({
            attributeId,
            ...results[attributeId],
          })),
        },
      },
    });

    onGameFinished(String(data?.responseCreate.slug) ?? "");
  };

  useEffect(() => {
    const { shouldChangeRound, round } = state.status;
    if (shouldChangeRound && round === 0) {
      dispatch({ type: "NEXT_ROUND" });
    } else {
      setTimeout(() => dispatch({ type: "NEXT_ROUND" }), 400);
    }
  }, [state.status.shouldChangeRound]);

  useEffect(() => {
    if (state.status.isGameFinished) {
      saveResponse();
    }
  }, [state.status.isGameFinished]);

  return <RankifierStateContext.Provider value={[state, dispatch]}>{children}</RankifierStateContext.Provider>;
};
